import type { ReportRange } from '@hbs-us/contracts';
import { Button, cn, useTranslation } from '@hbs-us/ui';
import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, useId, useState } from 'react';
import type { ReportsSearch } from '../searchParams.js';

/**
 * Date-range filter (legacy ReportsPage 80–112): the preset range pills
 * (last 7 days / last 30 days / last 12 months; rolling windows the api computes in
 * `reporting.ts`, not calendar periods) plus the custom Start/End date form.
 *
 * The range lives in typed search params (Discretion #1) — selecting a pill or
 * applying a custom window NAVIGATES with new search params, which triggers
 * exactly one TanStack Query refetch of the coarse endpoint. The active pill is
 * the one place the orange accent is used here (`bg-primary`); Apply is the
 * single primary action on the custom form.
 */

const PRESET_RANGES: Exclude<ReportRange, 'custom'>[] = ['1w', '1m', '1y'];

export function RangeFilter({ search }: { search: ReportsSearch }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const startId = useId();
  const endId = useId();

  const [start, setStart] = useState(search.start ?? '');
  const [end, setEnd] = useState(search.end ?? '');

  const selectRange = (range: Exclude<ReportRange, 'custom'>) => {
    void navigate({ to: '/reports', search: { range } });
  };

  const applyCustom = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void navigate({ to: '/reports', search: { range: 'custom', start, end } });
  };

  return (
    <section
      aria-label={t('reports:filter.apply')}
      className="mb-4 rounded-md border border-border bg-card p-6"
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {PRESET_RANGES.map((range) => {
          const active = search.range === range;
          return (
            <button
              key={range}
              type="button"
              aria-pressed={active}
              onClick={() => selectRange(range)}
              className={cn(
                'inline-flex h-11 items-center rounded-md border px-4 font-medium text-sm transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                active
                  ? 'border-transparent bg-primary text-primary-foreground'
                  : 'border-border bg-card text-foreground hover:bg-secondary',
              )}
            >
              {t(`reports:filter.ranges.${range}`)}
            </button>
          );
        })}
      </div>

      <form onSubmit={applyCustom} className="flex max-w-xl flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor={startId} className="font-medium text-foreground text-xs">
            {t('reports:filter.startLabel')}
          </label>
          <input
            id={startId}
            type="date"
            name="start"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            required
            className="h-11 rounded-md border border-input bg-background px-3 font-mono text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={endId} className="font-medium text-foreground text-xs">
            {t('reports:filter.endLabel')}
          </label>
          <input
            id={endId}
            type="date"
            name="end"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            required
            className="h-11 rounded-md border border-input bg-background px-3 font-mono text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
        <Button type="submit">{t('reports:filter.apply')}</Button>
      </form>
    </section>
  );
}
